// OverrideModal — shared admin-override dialog (Phase 1).
//
// One reusable Chakra modal for every admin correction control (cancel/revoke,
// edit) across the HR domain, so the reason field, confirmation, and busy/error
// handling are identical everywhere (no per-screen duplication). Follows the
// Chakra Modal precedent from Pages/Admin/AdminPanel.jsx.
//
// Props:
//   isOpen, onClose
//   title            modal heading
//   description      optional context line (e.g. "Pratiksha · 2026-09-16")
//   confirmLabel     confirm button text (default "Confirm")
//   confirmColorScheme  Chakra color scheme for the confirm button (default "red")
//   reasonLabel      label for the mandatory reason field
//   fields           optional edit fields: [{ name, label, type, options?, value, help? }]
//                    type ∈ "text" | "textarea" | "date" | "select"
//   onSubmit         async ({ reason, values }) => void   (throw to keep modal open)
//
// The reason is always required. onSubmit runs with a busy spinner; if it throws
// the modal stays open (the caller shows the toast), otherwise it closes.

import React, { useEffect, useState } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, Textarea, Input, Select, FormControl, FormLabel,
  FormHelperText, VStack, Text,
} from "@chakra-ui/react";

export default function OverrideModal({
  isOpen,
  onClose,
  title,
  description,
  confirmLabel = "Confirm",
  confirmColorScheme = "red",
  reasonLabel = "Reason (required)",
  fields = [],
  onSubmit,
}) {
  const [reason, setReason] = useState("");
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setReason("");
      const initial = {};
      for (const f of fields) initial[f.name] = f.value ?? "";
      setVals(initial);
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const reasonMissing = !reason.trim();

  async function submit() {
    if (reasonMissing || busy) return;
    setBusy(true);
    try {
      await onSubmit({ reason: reason.trim(), values: vals });
      onClose();
    } catch (e) {
      // caller surfaces the error; keep the modal open so the admin can retry
      setBusy(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={busy ? () => {} : onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton isDisabled={busy} />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            {description ? (
              <Text fontSize="sm" color="gray.500">{description}</Text>
            ) : null}

            {fields.map((f) => (
              <FormControl key={f.name}>
                <FormLabel fontSize="sm">{f.label}</FormLabel>
                {f.type === "textarea" ? (
                  <Textarea
                    value={vals[f.name] ?? ""}
                    onChange={(e) => setVals((v) => ({ ...v, [f.name]: e.target.value }))}
                    rows={3}
                  />
                ) : f.type === "select" ? (
                  <Select
                    value={vals[f.name] ?? ""}
                    onChange={(e) => setVals((v) => ({ ...v, [f.name]: e.target.value }))}
                  >
                    {(f.options || []).map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Select>
                ) : (
                  <Input
                    type={f.type === "date" ? "date" : "text"}
                    value={vals[f.name] ?? ""}
                    onChange={(e) => setVals((v) => ({ ...v, [f.name]: e.target.value }))}
                  />
                )}
                {f.help ? <FormHelperText>{f.help}</FormHelperText> : null}
              </FormControl>
            ))}

            <FormControl isRequired isInvalid={reasonMissing}>
              <FormLabel fontSize="sm">{reasonLabel}</FormLabel>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why is this change being made? (recorded in the audit log)"
                rows={3}
                autoFocus
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={busy}>Cancel</Button>
          <Button
            colorScheme={confirmColorScheme}
            onClick={submit}
            isLoading={busy}
            isDisabled={reasonMissing}
          >
            {confirmLabel}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
